export type CrmProspectSegment =
  | "master_builder"
  | "architect"
  | "company"
  | "municipality"
  | "individual"
  | "other";

export type CrmImportPreviewRow = {
  rowNumber: number;
  selected: boolean;
  valid: boolean;
  issues: string[];
  title: string;
  prospect_segment: CrmProspectSegment;
  company_name: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_title: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  postal_code: string;
  city: string;
  need_summary: string;
  expected_value_ex_vat: string;
  source: string;
  next_action_at: string;
  notes: string;
};

const aliases: Record<string, string[]> = {
  prospect_segment: ["type_prospect", "segment", "categorie", "catégorie", "prospect_segment"],
  company_name: ["societe", "société", "entreprise", "company_name"],
  contact_first_name: ["prenom", "prénom", "contact_first_name"],
  contact_last_name: ["nom", "contact_last_name"],
  contact_title: ["fonction", "poste", "contact_title"],
  contact_email: ["email", "e-mail", "mail", "contact_email"],
  contact_phone: ["telephone", "téléphone", "tel", "contact_phone"],
  address: ["adresse", "address"],
  postal_code: ["code_postal", "cp", "postal_code"],
  city: ["ville", "city"],
  need_summary: ["besoin", "besoin_resume", "need_summary"],
  expected_value_ex_vat: ["valeur_estimee_ht", "valeur_ht", "expected_value_ex_vat"],
  source: ["origine", "source", "canal"],
  next_action_at: ["prochaine_action", "next_action_at"],
  notes: ["notes", "commentaire"],
  title: ["opportunite", "opportunité", "titre", "title"],
};

const segmentAliases: Record<string, CrmProspectSegment> = {
  "maitre d'oeuvre": "master_builder",
  "maître d'oeuvre": "master_builder",
  "maitre d’œuvre": "master_builder",
  "maître d’œuvre": "master_builder",
  moe: "master_builder",
  architecte: "architect",
  architect: "architect",
  entreprise: "company",
  societe: "company",
  société: "company",
  commune: "municipality",
  collectivite: "municipality",
  collectivité: "municipality",
  particulier: "individual",
  individual: "individual",
  autre: "other",
  other: "other",
};

function normalizeHeader(value: string) {
  return value.trim().toLocaleLowerCase("fr").replace(/\s+/g, "_");
}

function parseLine(line: string, separator: string) {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (character === separator && !quoted) {
      cells.push(current.trim());
      current = "";
    } else current += character;
  }
  cells.push(current.trim());
  return cells;
}

function readField(row: Record<string, string>, field: string) {
  const names = aliases[field] || [field];
  for (const name of names) {
    const value = row[normalizeHeader(name)];
    if (value != null) return value.trim();
  }
  return "";
}

function normalizeSegment(value: string): CrmProspectSegment {
  const normalized = value.trim().toLocaleLowerCase("fr");
  return segmentAliases[normalized] ||
    (["master_builder", "architect", "company", "municipality", "individual", "other"].includes(normalized)
      ? normalized as CrmProspectSegment
      : "other");
}

export function parseCrmProspectFile(content: string): CrmImportPreviewRow[] {
  const lines = content.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const separator = (lines[0].match(/;/g) || []).length >= (lines[0].match(/,/g) || []).length ? ";" : ",";
  const headers = parseLine(lines[0], separator).map(normalizeHeader);
  return lines.slice(1).map((line, index) => {
    const values = parseLine(line, separator);
    const sourceRow = Object.fromEntries(headers.map((header, cellIndex) => [header, values[cellIndex] || ""]));
    const company = readField(sourceRow, "company_name");
    const firstName = readField(sourceRow, "contact_first_name");
    const lastName = readField(sourceRow, "contact_last_name");
    const email = readField(sourceRow, "contact_email").toLocaleLowerCase("fr");
    const phone = readField(sourceRow, "contact_phone");
    const city = readField(sourceRow, "city");
    const title = readField(sourceRow, "title") || company || [firstName, lastName].filter(Boolean).join(" ");
    const issues: string[] = [];
    if (!title) issues.push("Société ou contact obligatoire");
    if (!email && !phone && !(company && city)) issues.push("E-mail, téléphone ou société + ville obligatoire");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) issues.push("Adresse e-mail invalide");
    const expectedValue = readField(sourceRow, "expected_value_ex_vat").replace(",", ".");
    if (expectedValue && !/^\d+(\.\d+)?$/.test(expectedValue)) issues.push("Valeur HT invalide");
    const nextActionAt = readField(sourceRow, "next_action_at");
    if (nextActionAt && Number.isNaN(new Date(nextActionAt).getTime())) issues.push("Date de prochaine action invalide");
    return {
      rowNumber: index + 2,
      selected: issues.length === 0,
      valid: issues.length === 0,
      issues,
      title,
      prospect_segment: normalizeSegment(readField(sourceRow, "prospect_segment")),
      company_name: company,
      contact_first_name: firstName,
      contact_last_name: lastName,
      contact_title: readField(sourceRow, "contact_title"),
      contact_email: email,
      contact_phone: phone,
      address: readField(sourceRow, "address"),
      postal_code: readField(sourceRow, "postal_code"),
      city,
      need_summary: readField(sourceRow, "need_summary"),
      expected_value_ex_vat: expectedValue,
      source: readField(sourceRow, "source"),
      next_action_at: nextActionAt,
      notes: readField(sourceRow, "notes"),
    };
  });
}

export const crmImportTemplate = [
  "type_prospect;societe;prenom;nom;fonction;email;telephone;adresse;code_postal;ville;besoin;valeur_estimee_ht;origine;prochaine_action;notes",
  "architecte;Atelier Exemple;Camille;Martin;Architecte;camille@example.fr;0600000000;1 rue Exemple;83310;Grimaud;Partenariat fin de chantier;12000;Prospection directe;;À qualifier",
  "commune;Commune Exemple;;;Services techniques;;0494000000;Place de la Mairie;83120;Sainte-Maxime;Entretien bâtiments communaux;;Réseau;;",
].join("\n");
